import {
  LiveChatProvider,
  ResourceCenterBlockType,
  ResourceCenterData,
  ResourceCenterLiveChatBlock,
} from '@usertour/types';
import { executeUserCode } from '@usertour/helpers';
import { logger, timerManager } from '@/utils';

const log = logger.scope('live-chat');
// ============================================================================
// Callbacks — the manager notifies the owner when provider state changes
// ============================================================================

export interface LiveChatManagerCallbacks {
  /** Called when the provider's chat widget is closed autonomously (by the user, not by force-close) */
  onProviderClose: () => void;
  /**
   * Returns whether JavaScript evaluation has been disabled via `usertour.disableEvalJs()`.
   * When it returns true, the CUSTOM provider's `customLiveChatCode` is skipped instead of
   * executed via `new Function(...)`. Mirrors the guard used for JAVASCRIPT_EVALUATE actions.
   */
  isEvalJsDisabled?: () => boolean;
}

// ============================================================================
// UsertourLiveChatManager
// ============================================================================

export class UsertourLiveChatManager {
  private activeBlock: ResourceCenterLiveChatBlock | null = null;
  private sessionCleanup: (() => void) | null = null;

  constructor(private readonly callbacks: LiveChatManagerCallbacks) {}

  /** Whether a live chat provider session is currently active */
  get isActive(): boolean {
    return this.activeBlock !== null;
  }

  /**
   * Hide native launchers for all live chat providers in the resource center data.
   * Prevents duplicate floating buttons when the resource center takes over the launcher role.
   */
  configure(data: ResourceCenterData | undefined): void {
    if (!data) return;

    const configured = new Set<LiveChatProvider>();
    for (const tab of data.tabs) {
      for (const block of tab.blocks) {
        if (block.type === ResourceCenterBlockType.LIVE_CHAT) {
          const provider = (block as ResourceCenterLiveChatBlock).liveChatProvider;
          if (!configured.has(provider)) {
            configured.add(provider);
            this.hideLauncher(provider);
          }
        }
      }
    }
  }

  /**
   * Whether the provider's script is actually present on the host page. Every
   * provider call below uses optional chaining, so opening a missing provider
   * is a SILENT no-op — and its close listener never attaches, which means the
   * caller would hide the resource center and nothing would ever restore it.
   * Check availability up front instead.
   */
  isProviderAvailable(provider: LiveChatProvider): boolean {
    const w = window as any;
    switch (provider) {
      case LiveChatProvider.CRISP:
        return !!w.$crisp;
      case LiveChatProvider.INTERCOM:
        return typeof w.Intercom === 'function';
      case LiveChatProvider.ZENDESK_CLASSIC:
      case LiveChatProvider.ZENDESK_MESSENGER:
        return typeof w.zE === 'function';
      case LiveChatProvider.FRESHCHAT:
        return !!(w.FreshworksWidget ?? w.fcWidget ?? w.fdWidget);
      case LiveChatProvider.HELP_SCOUT:
        return typeof w.Beacon === 'function';
      case LiveChatProvider.HUBSPOT:
        // The loader script may not have exposed HubSpotConversations yet —
        // accept its settings object or container as evidence it's installed;
        // openHubSpot's ready-polling (now with a deadline) covers the rest.
        return !!(
          w.HubSpotConversations ??
          w.hsConversationsSettings ??
          document.getElementById(UsertourLiveChatManager.HUBSPOT_CONTAINER_ID)
        );
      case LiveChatProvider.CUSTOM:
        return true;
      default:
        return false;
    }
  }

  /**
   * Open a live chat provider widget and listen for close events.
   * Automatically tears down any previously active session.
   *
   * Returns false (and does nothing) when the provider's script is not present
   * on the page — callers must NOT hide their own UI in that case, since no
   * close event would ever arrive to restore it.
   *
   * Does not handle CUSTOM — that is fire-and-forget with no session to
   * track; use `executeCustomCode` instead.
   */
  open(block: ResourceCenterLiveChatBlock): boolean {
    if (!this.isProviderAvailable(block.liveChatProvider)) {
      log.warn(
        `Live chat provider "${block.liveChatProvider}" was not detected on this page; install its script on the host page or the chat cannot open`,
      );
      return false;
    }
    this.teardownSession();
    this.activeBlock = block;

    if (block.liveChatProvider === LiveChatProvider.HUBSPOT) {
      this.sessionCleanup = this.openHubSpot(() => this.handleProviderClose());
    } else {
      this.openWidget(block);
      this.sessionCleanup = this.listenClose(block, () => this.handleProviderClose());
    }
    return true;
  }

  /**
   * Run a CUSTOM live chat block's user-provided code. Fire-and-forget:
   * no session state is tracked and no close listener is attached, so the
   * resource center stays interactive afterwards.
   */
  executeCustomCode(block: ResourceCenterLiveChatBlock): void {
    if (block.liveChatProvider !== LiveChatProvider.CUSTOM) return;
    if (this.callbacks.isEvalJsDisabled?.()) {
      log.warn('JavaScript evaluation is disabled; the custom live chat code was skipped');
      return;
    }
    if (block.customLiveChatCode) {
      executeUserCode(block.customLiveChatCode);
    }
  }

  /**
   * Force-close the active live chat provider widget.
   * Does NOT trigger onProviderClose callback — the caller manages its own state update.
   */
  close(): void {
    if (!this.activeBlock) return;

    this.closeWidget(this.activeBlock);
    this.teardownSession();
    this.activeBlock = null;
  }

  /** Release all resources (timers, observers, listeners) */
  dispose(): void {
    if (this.activeBlock) {
      this.closeWidget(this.activeBlock);
    }
    this.teardownSession();
    this.activeBlock = null;
  }

  // ── Provider close handling ────────────────────────────────────────

  private handleProviderClose(): void {
    this.teardownSession();
    this.activeBlock = null;
    this.callbacks.onProviderClose();
  }

  private teardownSession(): void {
    this.sessionCleanup?.();
    this.sessionCleanup = null;
  }

  // ── Hide native launcher per provider ──────────────────────────────

  private hideLauncher(provider: LiveChatProvider): void {
    const w = window as any;
    switch (provider) {
      case LiveChatProvider.CRISP:
        w.$crisp?.push(['do', 'chat:hide']);
        break;
      case LiveChatProvider.INTERCOM:
        w.Intercom?.('update', { hide_default_launcher: true });
        break;
      case LiveChatProvider.ZENDESK_CLASSIC:
        w.zE?.('webWidget', 'hide');
        break;
      case LiveChatProvider.ZENDESK_MESSENGER:
        // Zendesk Messenger has no native hide API; launcher is controlled by open/close only
        break;
      case LiveChatProvider.FRESHCHAT:
        // Freshchat has no reliable hide-launcher-only API; open/close manages visibility
        break;
      case LiveChatProvider.HELP_SCOUT:
        w.Beacon?.('config', { display: { style: 'manual' } });
        break;
      case LiveChatProvider.HUBSPOT:
        this.setHubSpotVisibility('hidden');
        break;
    }
  }

  // ── Open provider widget ───────────────────────────────────────────

  private openWidget(block: ResourceCenterLiveChatBlock): void {
    const w = window as any;
    switch (block.liveChatProvider) {
      case LiveChatProvider.CRISP:
        w.$crisp?.push(['do', 'chat:show']);
        w.$crisp?.push(['do', 'chat:open']);
        break;
      case LiveChatProvider.INTERCOM:
        w.Intercom?.('update', { hide_default_launcher: false });
        w.Intercom?.('show');
        break;
      case LiveChatProvider.ZENDESK_CLASSIC:
        w.zE?.('webWidget', 'show');
        w.zE?.('webWidget', 'open');
        break;
      case LiveChatProvider.ZENDESK_MESSENGER:
        w.zE?.('messenger', 'open');
        break;
      case LiveChatProvider.FRESHCHAT:
        if (w.FreshworksWidget) {
          w.FreshworksWidget('open');
        } else {
          (w.fcWidget ?? w.fdWidget)?.open?.();
        }
        break;
      case LiveChatProvider.HELP_SCOUT:
        w.Beacon?.('config', { display: { style: 'icon' } });
        w.Beacon?.('open');
        break;
    }
  }

  // ── Close provider widget ──────────────────────────────────────────

  private closeWidget(block: ResourceCenterLiveChatBlock): void {
    const w = window as any;
    switch (block.liveChatProvider) {
      case LiveChatProvider.CRISP:
        w.$crisp?.push(['do', 'chat:hide']);
        w.$crisp?.push(['do', 'chat:close']);
        break;
      case LiveChatProvider.INTERCOM:
        w.Intercom?.('update', { hide_default_launcher: true });
        w.Intercom?.('hide');
        break;
      case LiveChatProvider.ZENDESK_CLASSIC:
        w.zE?.('webWidget', 'hide');
        w.zE?.('webWidget', 'close');
        break;
      case LiveChatProvider.ZENDESK_MESSENGER:
        w.zE?.('messenger', 'close');
        break;
      case LiveChatProvider.FRESHCHAT:
        if (w.FreshworksWidget) {
          w.FreshworksWidget('close');
        } else {
          (w.fcWidget ?? w.fdWidget)?.close?.();
        }
        break;
      case LiveChatProvider.HELP_SCOUT:
        w.Beacon?.('config', { display: { style: 'manual' } });
        w.Beacon?.('close');
        break;
      case LiveChatProvider.HUBSPOT:
        this.setHubSpotVisibility('hidden');
        w.HubSpotConversations?.widget?.close?.();
        break;
    }
  }

  // ── Listen for provider close events ───────────────────────────────

  private listenClose(
    block: ResourceCenterLiveChatBlock,
    onClose: () => void,
  ): (() => void) | null {
    const w = window as any;
    switch (block.liveChatProvider) {
      case LiveChatProvider.CRISP:
        w.$crisp?.push(['on', 'chat:closed', onClose]);
        return () => w.$crisp?.push(['off', 'chat:closed']);
      case LiveChatProvider.INTERCOM:
        w.Intercom?.('onHide', onClose);
        return null;
      case LiveChatProvider.ZENDESK_CLASSIC:
        w.zE?.('webWidget:on', 'close', onClose);
        return () => w.zE?.('webWidget:on', 'close', () => {});
      case LiveChatProvider.ZENDESK_MESSENGER:
        w.zE?.('messenger:on', 'close', onClose);
        return () => w.zE?.('messenger:on', 'close', () => {});
      case LiveChatProvider.FRESHCHAT:
        if (w.FreshworksWidget) {
          w.FreshworksWidget('onClose', onClose);
        } else {
          (w.fcWidget ?? w.fdWidget)?.on?.('widget:closed', onClose);
        }
        return null;
      case LiveChatProvider.HELP_SCOUT:
        w.Beacon?.('on', 'close', onClose);
        return () => w.Beacon?.('off', 'close');
      default:
        return null;
    }
  }

  // ── HubSpot: MutationObserver + CSS visibility + ready polling ─────

  private static readonly HUBSPOT_CONTAINER_ID = 'hubspot-messages-iframe-container';
  private static readonly HUBSPOT_POLL_TIMER_ID = 'live-chat-hubspot-poll';

  private setHubSpotVisibility(value: 'visible' | 'hidden'): void {
    document
      .getElementById(UsertourLiveChatManager.HUBSPOT_CONTAINER_ID)
      ?.style.setProperty('visibility', value, 'important');
  }

  /** How long to wait for HubSpot to become ready before giving the UI back. */
  private static readonly HUBSPOT_READY_TIMEOUT_MS = 10_000;

  private openHubSpot(onClose: () => void): () => void {
    const timerId = UsertourLiveChatManager.HUBSPOT_POLL_TIMER_ID;
    const startedAt = Date.now();
    let observer: MutationObserver | null = null;
    let disposed = false;

    const setup = () => {
      const w = window as any;
      const container = document.getElementById(UsertourLiveChatManager.HUBSPOT_CONTAINER_ID);
      if (!w.HubSpotConversations || !container) {
        // Never became ready — stop polling (the old code polled forever) and
        // report a close so the caller restores its UI instead of staying
        // hidden behind a chat that will never open.
        if (Date.now() - startedAt > UsertourLiveChatManager.HUBSPOT_READY_TIMEOUT_MS) {
          timerManager.clearInterval(timerId);
          if (!disposed) {
            log.warn('HubSpot live chat did not become ready in time; restoring the UI');
            onClose();
          }
        }
        return;
      }

      // API and DOM both ready — stop polling
      timerManager.clearInterval(timerId);
      if (disposed) return;

      // Open the chat
      this.setHubSpotVisibility('visible');
      w.HubSpotConversations.widget.open();

      // Detect close via MutationObserver on .hs-shadow-container.active
      let isOpen = true;
      const checkState = () => {
        const active = !!container.querySelector('.hs-shadow-container.active');
        if (isOpen && !active) {
          isOpen = false;
          this.setHubSpotVisibility('hidden');
          onClose();
        } else if (!isOpen && active) {
          isOpen = true;
        }
      };

      observer = new MutationObserver(checkState);
      observer.observe(container, {
        attributes: true,
        subtree: true,
        attributeFilter: ['class'],
      });
    };

    // Poll every 100ms until HubSpotConversations + DOM are ready
    timerManager.setInterval(timerId, setup, 100);
    setup();

    return () => {
      disposed = true;
      timerManager.clearInterval(timerId);
      observer?.disconnect();
      observer = null;
    };
  }
}
